import Component, { mixins } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import TLabel from '@/lib/form/label/Label.vue';
import TInput from '@/lib/form/input/Input.vue';
import TButton from '@/lib/form/button/Button.vue';
import CDropdownToggleMixin from '@/lib/form/dropdown/dropdownToggleMixin';

@Component({
  name: 'm-select',
  props: {
    native: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    size: {
      type: String
    },
    variant: {
      type: String,
      validator: function (value) {
        // The value must match one of these strings
        return ['text', 'subtle'].indexOf(value) !== -1;
      }
    },
    required: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number]
    },
    placeholder: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    invalidMessage: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => [String, Number]
    },
    searchable: {
      type: Boolean,
      default: false
    },
    placement: {
      type: String,
      default: ''
    },
    browser: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    truncate: {
      type: Boolean,
      default: false
    }
  },
  components: {
    TLabel,
    TInput,
    TButton
  },
  model: {
    prop: 'value',
    event: 'update'
  },
  mixins: [CDropdownToggleMixin]
})
export default class MSelect extends mixins(CDropdownToggleMixin) {
  disabled!: boolean;
  readonly!: boolean;
  searchable!: boolean;
  size!: string;
  name!: string;
  value!: string | number;
  placeholder!: string;
  isInvalid!: boolean;
  invalidMessage!: string;
  options!: any[];
  variant!: string;
  placement!: string;
  native!: boolean;
  private label!: string;

  private internalValue: any;
  private searchValue: any;
  private browser: any;
  formName!: string;
  rules!: any;

  constructor() {
    super();
    this.internalValue = this.value || this.value === 0 ? this.value : '';
    this.searchValue = '';
  }

  created() {
    if (this.$parent.$props && this.$parent.$props.formName && this.form) {
      this.form.register(this.name, this.internalValue, this.rules);
      this.form.update(this.name, this.internalValue, this.rules);
    }
  }

  destroyed() {
    this.form && this.form.unregister(this.name);
  }

  get form() {
    return (
      this.$form &&
      this.$form[this.$parent.$props && this.$parent.$props.formName]
    );
  }

  get classList() {
    return {
      't-select': true,
      [`t-select--${this.size}`]: this.size,
      [`t-select--${this.placement}`]: this.placement,
      't-select--disabled': this.disabled || this.readonly,
      't-select--invalid': this.isInvalid || this.invalidMessage,
      [`t-select--${this.variant}`]: Boolean(this.variant),
      [`t-select--native`]: Boolean(this.native)
    };
  }

  get internalPlaceholder(): string {
    if (this.internalValue || this.internalValue === 0) {
      const currentOption = this.options.find(option =>
        option.value || option.value === 0
          ? option.value === this.internalValue
          : option === this.internalValue
      );
      return currentOption && (currentOption.displayText || currentOption);
    }
    return this.placeholder || '';
  }

  get filteredOptions() {
    if (this.searchable || this.searchValue) {
      return this.options.filter(option => {
        const displayText = (option.displayText || option || '').toLowerCase();
        return displayText.startsWith(this.searchValue.toLowerCase());
      });
    }
    return this.options || [];
  }

  get showHeader(): boolean {
    return this.searchable;
  }

  get areOptionsEmpty(): boolean {
    return this.filteredOptions.length === 0;
  }

  saveValue() {
    if (this.searchValue) {
      this.$emit('addNew');
    }
  }

  updateValue(newValue: string | number, emit = true) {
    if (this.internalValue === newValue) {
      return;
    }
    this.internalValue = newValue;
    if (emit) {
      this.$emit('update', this.internalValue, this.name);
      this.$emit('change', this.internalValue);
      this.form &&
        this.form.update(
          this.name,
          this.internalValue,
          this.rules
        );
    }
  }

  selectItemHandler(option) {
    this.updateValue(option.value || option, true);
    this.hide();
  }

  change(event) {
    this.updateValue(event.target.value, true);
  }

  @Watch('value')
  updateInternalValue(newValue) {
    this.updateValue(newValue);
  }

  searchHandler(newValue: string) {
    this.searchValue = newValue;
    this.$emit('search', this.searchValue);
  }

  preventEvent(event) {
    event.preventDefault();
  }

  onEnter(event) {
    event.preventDefault();
    this.$emit('onenter', event);
  }
}
